import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminDeleteImageSource,
  adminImportWordPressSources,
  adminListImageSources,
  type ImageSourceRecord,
} from "@/lib/image-sources.functions";
import { adminSaveImageSource } from "@/lib/image-sources.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/images")({
  component: AdminImageSources,
});

type Filter = "all" | "missing" | "recorded";

const ORIGINS = ["unknown", "original", "stock", "client", "third-party"] as const;

// Source assets are fingerprinted when the site is deployed, so their repo paths
// are not public URLs in production. Vite resolves this map to the real deployed
// URLs while preserving the original path as the inventory key.
const BUNDLED_IMAGE_URLS = import.meta.glob(
  "/src/assets/**/*.{png,jpg,jpeg,webp,gif,svg,avif}",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;

function previewUrl(src: string): string {
  return BUNDLED_IMAGE_URLS[src] ?? src;
}

function legacyPreviewUrl(src: string): string | null {
  const match = src.match(/^\/__l5e\/assets-v1\/([0-9a-f-]{36})\//i);
  return match?.[1] ? `/img/admin-previews/${match[1]}.webp` : null;
}

const LOCAL_PREVIEW_BY_FILENAME: Record<string, string> = {
  "FUE-NO-SCAR-STRIP-e1651161519818.jpg": "/img/admin-previews/FUE-NO-SCAR-STRIP-e1651161519818.jpg",
  "IMG_7607-1-scaled-e1651161651179.jpg": "/img/admin-previews/IMG_7607-1-scaled-e1651161651179.jpg",
  "logo-psychology-today-blue.svg": "/img/admin-previews/logo-psychology-today-blue.svg",
  "Medium_Logo_Monogram.svg": "/img/admin-previews/Medium_Logo_Monogram.png",
  "id-preview-39c96a4a--a5cd4ee2-ca39-4fc0-8cff-b0def42dd345.lovable.app-1779734674220.png": "/img/admin-previews/site-preview.png",
};

function fallbackPreviewUrl(src: string): string | null {
  const filename = src.split("?")[0]?.split("/").pop();
  return (filename && LOCAL_PREVIEW_BY_FILENAME[filename]) || legacyPreviewUrl(src);
}

function ImageThumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const resolvedSrc = previewUrl(src.startsWith("//") ? `https:${src}` : src);
  const fallbackSrc = fallbackPreviewUrl(src);
  return (
    <div className="flex h-32 items-center justify-center overflow-hidden rounded border border-border bg-neutral-400 px-2 text-center">
      {failed ? (
        <span className="text-xs text-muted-foreground">Preview unavailable</span>
      ) : (
        <img
          src={usingFallback ? fallbackSrc ?? resolvedSrc : resolvedSrc}
          alt=""
          loading="lazy"
          className="max-h-32 object-contain"
          onError={() => {
            if (!usingFallback && fallbackSrc) {
              setUsingFallback(true);
              return;
            }
            setFailed(true);
          }}
        />
      )}
    </div>
  );
}

function AdminImageSources() {
  const list = useServerFn(adminListImageSources);
  const save = useServerFn(adminSaveImageSource);
  const remove = useServerFn(adminDeleteImageSource);
  const importWp = useServerFn(adminImportWordPressSources);
  const qc = useQueryClient();

  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(60);
  const [editing, setEditing] = useState<ImageSourceRecord | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["imageSources"],
    queryFn: () => list(),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: (rec: ImageSourceRecord) =>
      save({
        data: {
          image_url: rec.image_url,
          filename: rec.filename,
          source_text: rec.source_text,
          use_standard_source: rec.use_standard_source,
          source_url: rec.source_url,
          owner_name: rec.owner_name,
          license_type: rec.license_type,
          license_reference: rec.license_reference,
          acquired_at: rec.acquired_at || null,
          origin: rec.origin as any,
          notes: rec.notes,
          used_on: rec.used_on,
        },
      }),
    onSuccess: (res, rec) => {
      // Keep the confirmed saved row in view. An immediate refetch can receive
      // an older edge response and incorrectly mark the image as missing again.
      qc.setQueryData(["imageSources"], (prev: any) => {
        if (!prev?.items) return prev;
        const items = prev.items.map((i: ImageSourceRecord) =>
          i.image_url === rec.image_url
            ? { ...i, ...res.item }
            : i,
        );
        return { ...prev, items, missing: items.filter((i: ImageSourceRecord) => !i.has_source).length };
      });
      setEditing(null);
      setSaveMsg(`Saved source for ${rec.filename ?? rec.image_url}.`);
    },
    onError: (e: any) => setSaveMsg(e?.message ?? "Could not save — please sign in again and retry."),
  });

  const deleteMut = useMutation({
    mutationFn: (image_url: string) => remove({ data: { image_url } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imageSources"] }),
  });

  const importMut = useMutation({
    mutationFn: () => importWp({ data: {} }),
    onSuccess: (res: any) => {
      setImportMsg(
        res.ok
          ? `Imported ${res.imported} images from WordPress — ${res.withSource} had a source recorded.`
          : res.message,
      );
      qc.invalidateQueries({ queryKey: ["imageSources"] });
    },
    onError: (e: any) => setImportMsg(e?.message ?? "Import failed."),
  });

  const items = q.data?.items ?? [];
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "missing" && i.has_source) return false;
      if (filter === "recorded" && !i.has_source) return false;
      if (!term) return true;
      return (
        (i.filename ?? "").toLowerCase().includes(term) ||
        i.image_url.toLowerCase().includes(term) ||
        (i.source_text ?? "").toLowerCase().includes(term) ||
        i.used_on.some((u) => u.toLowerCase().includes(term))
      );
    });
  }, [items, filter, search]);

  function exportCsv() {
    const head = [
      "filename", "image_url", "source", "use_standard_source", "source_url",
      "owner", "license_type", "license_reference", "acquired", "origin", "notes", "used_on",
    ];
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      head.join(","),
      ...filtered.map((i) =>
        [
          i.filename, i.image_url, i.source_text, i.use_standard_source, i.source_url,
          i.owner_name, i.license_type, i.license_reference, i.acquired_at, i.origin,
          i.notes, i.used_on.join(" | "),
        ].map(esc).join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `image-sources-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Image sources</h1>
          <p className="text-sm text-muted-foreground">
            {q.data
              ? `${q.data.total} images on the site · ${q.data.missing} still missing a source`
              : "Scanning the site…"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => importMut.mutate()}
            disabled={importMut.isPending}
          >
            {importMut.isPending ? "Importing…" : "Import from WordPress"}
          </Button>
        </div>
      </div>

      {importMsg && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
          {importMsg}
        </div>
      )}

      {saveMsg && (
        <div
          className={`rounded-md border p-3 text-sm ${
            saveMut.isError ? "border-destructive text-destructive" : "border-border bg-muted/40 text-foreground"
          }`}
        >
          {saveMsg}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search filename, page or source…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All images</SelectItem>
            <SelectItem value="missing">Missing a source</SelectItem>
            <SelectItem value="recorded">Source recorded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading images…</p>}
      {q.error && <p className="text-sm text-destructive">{(q.error as Error).message}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.slice(0, limit).map((item) => (
          <div key={item.image_url} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
            <ImageThumb src={item.image_url} />
            <div className="min-w-0 space-y-1">
              <p className="truncate text-sm font-medium text-foreground" title={item.filename ?? ""}>
                {item.filename}
              </p>
              <p className="truncate text-xs text-muted-foreground" title={item.used_on.join(", ")}>
                {item.used_on.length ? item.used_on.slice(0, 3).join(", ") : "Not currently used"}
                {item.used_on.length > 3 ? ` +${item.used_on.length - 3}` : ""}
              </p>
              <p className={`text-xs ${item.has_source ? "text-foreground" : "text-destructive"}`}>
                {item.has_source
                  ? item.use_standard_source
                    ? "Own image (standard source)"
                    : item.source_text || item.owner_name
                  : "No source recorded"}
              </p>
            </div>
            <div className="mt-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...item })}>
                Edit source
              </Button>
              {item.id && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteMut.mutate(item.image_url)}
                  disabled={deleteMut.isPending}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length > limit && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => setLimit((l) => l + 60)}>
            Load more ({filtered.length - limit} remaining)
          </Button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className="mt-8 w-full max-w-xl space-y-4 rounded-lg border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">Image source</h2>
                <p className="truncate text-xs text-muted-foreground">{editing.image_url}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Close</Button>
            </div>

            <ImageThumb src={editing.image_url} />

            <div className="flex items-center justify-between rounded border border-border p-3">
              <div>
                <Label className="text-sm">Use standard source</Label>
                <p className="text-xs text-muted-foreground">Our own photography / owned asset.</p>
              </div>
              <Switch
                checked={editing.use_standard_source}
                onCheckedChange={(v) => setEditing({ ...editing, use_standard_source: v, origin: v ? "original" : editing.origin })}
              />
            </div>

            <div className="space-y-1">
              <Label>Image source</Label>
              <Input
                value={editing.source_text ?? ""}
                placeholder="Photographer, agency or credit line"
                onChange={(e) => setEditing({ ...editing, source_text: e.target.value })}
              />
            </div>

            <div className="space-y-1">
              <Label>Image source URL</Label>
              <Input
                value={editing.source_url ?? ""}
                placeholder="https://…"
                onChange={(e) => setEditing({ ...editing, source_url: e.target.value })}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Owner / photographer</Label>
                <Input
                  value={editing.owner_name ?? ""}
                  onChange={(e) => setEditing({ ...editing, owner_name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Origin</Label>
                <Select
                  value={editing.origin}
                  onValueChange={(v) => setEditing({ ...editing, origin: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORIGINS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>License type</Label>
                <Input
                  value={editing.license_type ?? ""}
                  placeholder="Royalty-free, rights-managed, release on file…"
                  onChange={(e) => setEditing({ ...editing, license_type: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>License / receipt reference</Label>
                <Input
                  value={editing.license_reference ?? ""}
                  onChange={(e) => setEditing({ ...editing, license_reference: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Acquired date</Label>
                <Input
                  type="date"
                  value={editing.acquired_at ?? ""}
                  onChange={(e) => setEditing({ ...editing, acquired_at: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={editing.notes ?? ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </div>

            {editing.used_on.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Appears on: {editing.used_on.join(", ")}
              </p>
            )}

            {saveMut.error && (
              <p className="text-sm text-destructive">{(saveMut.error as Error).message}</p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => saveMut.mutate(editing)} disabled={saveMut.isPending}>
                {saveMut.isPending ? "Saving…" : "Save source"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
